import { isFunction } from 'lodash';

type ObjectWithAny = { [key: string]: any };

type FullStory = {
  identify: (id: string, userVars: ObjectWithAny) => void;
  setUserVars: (userVars: ObjectWithAny) => void;
  event: (eventName: string, eventProperties: ObjectWithAny) => void;
};

// Let TypeScript know that the global FS property may exist
declare global {
  interface Window {
    FS?: FullStory;
  }
}

const getFullStory = (): FullStory => window.FS as FullStory;

// See fullstory.html for details about when FS gets installed for a user.
export const isFullStoryInstalled = () =>
  !!window.FS && isFunction(getFullStory().identify);

export const identifyToFullStory = (
  id: string,
  userVars: { [key: string]: any }
) => {
  if (!isFullStoryInstalled()) {
    return; // bail silently, we don't want to throw an error here
  }

  getFullStory().identify(id, userVars);
};

// Note that custom vars need to be type-suffixed (e.g. _str for strings).
// See "Custom fields" section of this doc for more info: https://help.fullstory.com/develop-js/setuservars
export const setFullStoryUserVars = (userVars: { [key: string]: any }) => {
  if (!isFullStoryInstalled()) {
    return; // bail silently, we don't want to throw an error here
  }

  getFullStory().setUserVars(userVars);
};

// Note that both 'eventName' and 'eventProperties' here must conform to FullStory's requirements,
// otherwise your event will be silently rejected! The requirements are discussed here:
// https://help.fullstory.com/develop-js/363565-fs-event-api-sending-custom-event-data-into-fullstory#Limitations%20&%20Guidelines
export const sendFullStoryEvent = (
  eventName: string,
  eventProperties: { [key: string]: any } = {}
) => {
  if (!isFullStoryInstalled()) {
    return; // bail silently, we don't want to throw an error here
  }

  getFullStory().event(eventName, eventProperties);
};
